import {
  Alert,
  Button,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getEzCaterCaterers,
  getEzCaterMappableRestaurants,
  removeEzCaterCatererMapping,
  saveEzCaterCatererMapping,
} from 'api/ezcater';
import { captureException } from 'utils/errors';

const { Text } = Typography;

const SUBSCRIPTION_STATUS = {
  subscribed: { color: 'success', label: 'Subscribed' },
  error: { color: 'error', label: 'Subscription error' },
  pending: { color: 'processing', label: 'Pending' },
  not_mapped: { color: 'default', label: 'Not mapped' },
};

function formatStoreLabel(row) {
  const name = row.name || 'Unknown store';
  if (row.store_number) {
    return `${name} (#${row.store_number})`;
  }
  return name;
}

function formatRestaurantLabel(restaurant) {
  const name = restaurant?.name || 'Unknown restaurant';
  const number = restaurant?.restaurant_number ?? restaurant?.id;
  if (number) {
    return `${name} (#${number})`;
  }
  return name;
}

function EzCaterLocationMapping({ organizationId, integration, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [savingUuid, setSavingUuid] = useState(null);
  const [removingUuid, setRemovingUuid] = useState(null);
  const [caterers, setCaterers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selections, setSelections] = useState({});

  const load = useCallback(() => {
    if (!organizationId) return Promise.resolve();
    setLoading(true);
    return Promise.all([
      getEzCaterCaterers(organizationId),
      getEzCaterMappableRestaurants(organizationId),
    ])
      .then(([catererData, restaurantData]) => {
        const rows = catererData?.caterers || [];
        setCaterers(rows);
        setRestaurants(restaurantData?.restaurants || []);
        const nextSelections = {};
        rows.forEach((row) => {
          nextSelections[row.uuid] = row.mapped_restaurant_id || undefined;
        });
        setSelections(nextSelections);
      })
      .catch((e) => {
        captureException(e);
        message.error(
          e?.response?.data?.detail || e?.response?.data?.error || 'Failed to load ezCater locations',
        );
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const mappedRestaurantIds = useMemo(() => {
    const ids = new Set();
    caterers.forEach((row) => {
      if (row.mapped_restaurant_id) {
        ids.add(row.mapped_restaurant_id);
      }
    });
    return ids;
  }, [caterers]);

  const restaurantOptionsForRow = useCallback((row) => {
    const currentSelection = selections[row.uuid];
    return restaurants
      .filter((restaurant) => (
        !mappedRestaurantIds.has(restaurant.id)
        || restaurant.id === row.mapped_restaurant_id
        || restaurant.id === currentSelection
      ))
      .map((restaurant) => ({
        value: restaurant.id,
        label: formatRestaurantLabel(restaurant),
      }));
  }, [restaurants, mappedRestaurantIds, selections]);

  const handleSave = (catererRow) => {
    const restaurantId = selections[catererRow.uuid];
    if (!restaurantId) {
      message.error('Select a Nibble restaurant before saving.');
      return;
    }
    setSavingUuid(catererRow.uuid);
    saveEzCaterCatererMapping(organizationId, {
      caterer_uuid: catererRow.uuid,
      restaurant_id: restaurantId,
    })
      .then((data) => {
        message.success(`Mapped ${formatStoreLabel(catererRow)} to a Nibble restaurant.`);
        if (onUpdated) onUpdated(data);
        load();
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail || e?.response?.data?.error || 'Failed to save mapping');
      })
      .finally(() => setSavingUuid(null));
  };

  const handleUnmap = (catererRow) => {
    setRemovingUuid(catererRow.uuid);
    removeEzCaterCatererMapping(organizationId, catererRow.uuid)
      .then((data) => {
        message.success(`Removed mapping for ${formatStoreLabel(catererRow)}.`);
        if (onUpdated) onUpdated(data);
        load();
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail || e?.response?.data?.error || 'Failed to remove mapping');
      })
      .finally(() => setRemovingUuid(null));
  };

  const columns = [
    {
      title: 'ezCater store',
      key: 'store',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{formatStoreLabel(row)}</Text>
          {!row.accessible_in_token ? <Tag color="warning">Not in token</Tag> : null}
          {row.live ? <Tag color="green">Live</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Nibble restaurant',
      key: 'restaurant',
      render: (_, row) => (
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder={`Select restaurant for ${formatStoreLabel(row)}`}
          style={{ minWidth: 260 }}
          value={selections[row.uuid]}
          options={restaurantOptionsForRow(row)}
          onChange={(value) => setSelections((prev) => ({ ...prev, [row.uuid]: value }))}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, row) => {
        const meta = SUBSCRIPTION_STATUS[row.subscription_status] || SUBSCRIPTION_STATUS.not_mapped;
        return (
          <Space direction="vertical" size={0}>
            <Tag color={meta.color}>{meta.label}</Tag>
            {row.mapped_restaurant_name ? (
              <Text type="secondary">
                → {formatRestaurantLabel({
                  name: row.mapped_restaurant_name,
                  id: row.mapped_restaurant_id,
                })}
              </Text>
            ) : null}
            {row.subscription_error ? <Text type="danger">{row.subscription_error}</Text> : null}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button
            type="primary"
            size="small"
            loading={savingUuid === row.uuid}
            onClick={() => handleSave(row)}
          >
            Save
          </Button>
          {row.mapped_restaurant_id ? (
            <Popconfirm
              title={`Remove mapping for ${formatStoreLabel(row)}?`}
              description="ezCater orders for this store will stop ingesting until you map it again."
              okText="Unmap"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleUnmap(row)}
            >
              <Button
                size="small"
                danger
                loading={removingUuid === row.uuid}
              >
                Unmap
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Text style={{
        color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
      }}
      >
        Map each ezCater store to a Nibble catering restaurant by store name.
        Orders route by store; direct Nibble orders on mapped restaurants are unchanged.
      </Text>

      {integration?.mapping_summary_degraded && (
        <Alert
          type="warning"
          showIcon
          message="Could not refresh ezCater store list"
          description="Mapped location counts may be stale until ezCater is reachable again."
          style={{ marginBottom: 16 }}
        />
      )}

      {!integration?.ready_for_orders && !integration?.mapping_summary_degraded && (
        <Alert
          type="warning"
          showIcon
          message="No locations mapped yet"
          description="Orders will not ingest until at least one ezCater store is mapped to a Nibble restaurant."
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        size="small"
        rowKey="uuid"
        loading={loading}
        columns={columns}
        dataSource={caterers}
        pagination={false}
        locale={{ emptyText: 'No accessible ezCater stores found for this token.' }}
      />
    </div>
  );
}

export default EzCaterLocationMapping;
